import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  IAMAccountEntity,
  IAMAccountCredentialsDownloadedCmd
} from "@youm-front/iam-account/domain";
import { of } from "rxjs";
import {
  catchError,
  exhaustMap,
  mergeMap,
  startWith,
  switchMap
} from "rxjs/operators";
import { IAMAccountsActions } from "../actions";
import {
  IAMAccountCreateRequestDTO,
  IAMAccountCredentialsUpdateRequestDTO,
  IAMAccountDeleteRequestDTO,
  IAMAccountUpdateRequestDTO
} from "../dtos";
import { IAMAccountCredentialsService, IAMAccountsService } from "../services";

@Injectable()
export class IAMAccountsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly iamAccountsService: IAMAccountsService,
    private readonly iamAccountCredentialsService: IAMAccountCredentialsService
  ) {}

  iamAccountCredentialsDownload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountCredentialsDownload),
      switchMap((_) =>
        of(
          this.iamAccountCredentialsService.iamAccountCredentialsDownload(
            _.iamAccountCredentialsDownloadCmd
          )
        ).pipe(
          // To run pipe chain
          startWith(null),
          mergeMap(() => [
            IAMAccountsActions.iamAccountCredentialsDownloaded({
              iamAccountCredentialsDownloadedCmd:
                new IAMAccountCredentialsDownloadedCmd({
                  account: _.iamAccountCredentialsDownloadCmd.value.account
                })
            })
          ])
        )
      )
    )
  );

  iamAccountCredentialsUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountCredentialsUpdate),
      exhaustMap((_) =>
        this.iamAccountsService
          .iamAccountCredentialsUpdate(
            new IAMAccountCredentialsUpdateRequestDTO({
              accountId: _.iamAccountCredentialsUpdateCmd.value.accountId
            })
          )
          .pipe(
            mergeMap((_) => [
              IAMAccountsActions.iamAccountCredentialsUpdateSuccess({
                account: new IAMAccountEntity(_.data.account)
              })
            ]),
            catchError((_: Error) => [
              IAMAccountsActions.iamAccountCredentialsUpdateFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  iamAccountCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountCreate),
      exhaustMap((_) =>
        this.iamAccountsService
          .iamAccountCreate(
            new IAMAccountCreateRequestDTO({
              identity: _.iamAccountCreateCmd.value.identity
            })
          )
          .pipe(
            mergeMap((_) => [
              IAMAccountsActions.iamAccountCreateSuccess({
                account: new IAMAccountEntity(_.data.account)
              })
            ]),
            catchError((_: Error) => [
              IAMAccountsActions.iamAccountCreateFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  iamAccountDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountDelete),
      exhaustMap((_) =>
        this.iamAccountsService
          .iamAccountDelete(
            new IAMAccountDeleteRequestDTO({
              accountId: _.iamAccountDeleteCmd.value.accountId
            })
          )
          .pipe(
            mergeMap(() => [
              IAMAccountsActions.iamAccountDeleteSuccess({
                iamAccountDeleteCmd: _.iamAccountDeleteCmd
              })
            ]),
            catchError((_: Error) => [
              IAMAccountsActions.iamAccountDeleteFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  iamAccountsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountsList),
      switchMap(() =>
        this.iamAccountsService.iamAccountsList().pipe(
          mergeMap((_) => [
            IAMAccountsActions.iamAccountsListSuccess({
              accounts: _.data.accounts.map((_) => new IAMAccountEntity(_))
            })
          ]),
          catchError((_: Error) => [
            IAMAccountsActions.iamAccountsListFailure({
              error: _.message
            })
          ])
        )
      )
    )
  );

  iamAccountUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountsActions.iamAccountUpdate),
      exhaustMap((_) =>
        this.iamAccountsService
          .iamAccountUpdate(
            new IAMAccountUpdateRequestDTO({
              accountId: _.iamAccountUpdateCmd.value.accountId,
              identity: _.iamAccountUpdateCmd.value.identity
            })
          )
          .pipe(
            mergeMap((_) => [
              IAMAccountsActions.iamAccountUpdateSuccess({
                account: new IAMAccountEntity(_.data.account)
              })
            ]),
            catchError((_: Error) => [
              IAMAccountsActions.iamAccountUpdateFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );
}
